import { Injectable } from '@angular/core';
import { ONE_GB } from '../../shared/shared.constants';
import {
  ErrCode,
  StreamingSaveCapability,
  StreamingSaveDestination,
  StreamingSaveDestinationOutcome,
  StreamingSaveSeam,
  StreamingSaveTotalBytes,
  StreamingSaveUnavailableReason,
  STREAMING_SAVE_SIZE_UNKNOWN,
} from '../../shared/models';
import { LoggerService } from '../logger.service';
import { StreamingSaveSession } from './streaming-save-session';
import {
  STREAMING_SAVE_MIN_FILE_SIZE_GB,
  STREAMING_SAVE_PICKER_ID,
  STREAMING_SAVE_START_IN,
} from './streaming-save.constants';

const PICKER_CANCELLED = 'AbortError';
const MIN_STREAMING_BYTES = STREAMING_SAVE_MIN_FILE_SIZE_GB * ONE_GB;

@Injectable({
  providedIn: 'root',
})
export class StreamingSaveService {
  constructor(private loggerService: LoggerService) {}

  /**
   * The single gate — every caller asks this and nothing else. totalBytes is required so that no seam can be
   * un-gated by omission; pass STREAMING_SAVE_SIZE_UNKNOWN to say so deliberately, as a folder ZIP must.
   */
  public canStream(
    seam: StreamingSaveSeam,
    totalBytes: StreamingSaveTotalBytes,
    isLink = false,
  ): StreamingSaveCapability {
    if (this.isFramed()) {
      return this.unavailable(StreamingSaveUnavailableReason.FRAMED);
    }
    if (!window.isSecureContext) {
      return this.unavailable(StreamingSaveUnavailableReason.INSECURE_CONTEXT);
    }
    if (!this.hasPickerApi()) {
      return this.unavailable(StreamingSaveUnavailableReason.API_UNAVAILABLE);
    }
    if (!this.hasRemoveApi()) {
      return this.unavailable(StreamingSaveUnavailableReason.REMOVE_UNAVAILABLE);
    }
    if (totalBytes !== STREAMING_SAVE_SIZE_UNKNOWN && totalBytes < MIN_STREAMING_BYTES) {
      return this.unavailable(StreamingSaveUnavailableReason.BELOW_MIN_SIZE);
    }
    return { capable: true };
  }

  /**
   * Must be called synchronously in a user-gesture turn; transient activation expires after 5s.
   * No `types` filter: it makes the browser rewrite the extension to suit the mime type.
   */
  public async requestDestination(suggestedName: string): Promise<StreamingSaveDestination> {
    try {
      const handle = await window.showSaveFilePicker({
        suggestedName,
        startIn: STREAMING_SAVE_START_IN,
        id: STREAMING_SAVE_PICKER_ID,
      });
      return { outcome: StreamingSaveDestinationOutcome.GRANTED, handle };
    } catch (e) {
      if (e instanceof DOMException && e.name === PICKER_CANCELLED) {
        return { outcome: StreamingSaveDestinationOutcome.CANCELLED };
      }
      this.loggerService.error(`StreamingSaveService: save picker failed - ${e}`);
      return { outcome: StreamingSaveDestinationOutcome.FAILED, errcode: ErrCode.fromException(e) };
    }
  }

  /**
   * Separate from requestDestination so the gesture turn ends at the picker. Downloads are serial,
   * so callers defer this until the transfer item reaches the head of the queue.
   */
  public async openSession(handle: FileSystemFileHandle): Promise<StreamingSaveSession> {
    const writable = await handle.createWritable({ keepExistingData: false });
    return new StreamingSaveSession(handle, writable, this.loggerService);
  }

  /**
   * Removes a picked destination that never became a session. The picker truncates the chosen target at
   * confirm time, so an overwritten file's contents are already gone before this runs — deleting the
   * remaining husk is preferred over leaving an empty file under the user's original name.
   */
  public async discardDestination(handle: FileSystemFileHandle): Promise<void> {
    try {
      await handle.remove?.();
    } catch (e) {
      this.loggerService.warn(`StreamingSaveService: could not discard ${handle.name} - ${e}`);
    }
  }

  private unavailable(reason: StreamingSaveUnavailableReason): StreamingSaveCapability {
    return { capable: false, reason };
  }

  /**
   * Safari 26 and Firefox 111 ship createWritable for OPFS while shipping no picker, so probing
   * createWritable false-positives on exactly the browsers that must fall back silently.
   */
  private hasPickerApi(): boolean {
    return typeof window.showSaveFilePicker === 'function';
  }

  private hasRemoveApi(): boolean {
    return typeof FileSystemHandle !== 'undefined' && typeof FileSystemHandle.prototype?.remove === 'function';
  }

  private isFramed(): boolean {
    return window.top !== window.self;
  }
}
